import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowRight, CircleCheck, CirclePlay, CircleSlash, Clock, Map } from 'lucide-react'
import { env } from '@/lib/env'
import { endOfDayParam, formatDateTimeCompact, startOfDayParam } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { humanizeEnum, truncate } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import {
  PAYMENT_METHODS,
  PAYMENT_VERIFICATION_STATUSES,
  TRIP_STATUSES,
  type AdminTrip,
  type TripFilters,
} from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterDate, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { CancelBookingDialog } from '@/features/trip-requests/CancelBookingDialog'
import { PaymentAmountCell, PaymentMethodCell } from '@/features/payments/PaymentCell'
import { describePaymentMethod, describeVerificationStatus } from '@/features/payments/describe'
import { EditScheduleDialog, type ScheduleTarget } from '@/features/scheduling/EditScheduleDialog'
import { RouteMapDialog } from '@/features/maps/RouteMapDialog'
import { toRouteTarget, type RouteTarget } from '@/features/maps/geo'
import { endTrip, fetchTrips, startTrip } from './api'
import { AreaSurchargeBadge, LateChargeBadge } from '@/features/area-pricing/AreaSurchargeBadge'
import { describeAreaSurcharge, hasAreaChips, lateChargeExportValue } from '@/features/area-pricing/describe'
import { AddOnBadges } from './AddOnBadges'
import { describeAddOns } from './addOns'
import { TripDetailsDrawer } from './TripDetailsDrawer'
import styles from './TripsPage.module.css'

const DEFAULT_FILTERS = {
  search: '',
  status: '',
  paymentMethod: '',
  verificationStatus: '',
  from: '',
  to: '',
}

interface QuickAction {
  trip: AdminTrip
  action: 'start' | 'end'
}

export function TripsPage() {
  const table = useTableState({ defaultSort: 'startTime', defaultFilters: DEFAULT_FILTERS })
  const [selected, setSelected] = useState<AdminTrip | null>(null)
  const [quickAction, setQuickAction] = useState<QuickAction | null>(null)
  const [cancelling, setCancelling] = useState<number | null>(null)
  const [rescheduling, setRescheduling] = useState<ScheduleTarget | null>(null)
  const [viewingRoute, setViewingRoute] = useState<RouteTarget | null>(null)

  useEffect(() => {
    document.title = `Trips · ${env.appName} Operations`
  }, [])

  const filters = useMemo<TripFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      status: table.filters.status || undefined,
      paymentMethod: table.filters.paymentMethod || undefined,
      verificationStatus: table.filters.verificationStatus || undefined,
      from: table.filters.from ? startOfDayParam(table.filters.from) : undefined,
      to: table.filters.to ? endOfDayParam(table.filters.to) : undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const tripsQuery = useQuery({
    queryKey: queryKeys.trips.list(filters),
    queryFn: () => fetchTrips(filters),
    placeholderData: (previous) => previous,
  })

  // The drawer holds a snapshot; after a mutation the list is refetched, so the
  // open record is re-read from the fresh page rather than left stale.
  useEffect(() => {
    if (!selected || !tripsQuery.data) return
    const refreshed = tripsQuery.data.content.find((trip) => trip.id === selected.id)
    if (refreshed && refreshed !== selected) {
      setSelected(refreshed)
    }
  }, [tripsQuery.data, selected])

  const lifecycleMutation = useAdminMutation({
    mutationFn: ({ trip, action }: QuickAction) => (action === 'start' ? startTrip(trip.id) : endTrip(trip.id)),
    successTitle: 'Trip updated',
    invalidate: [
      queryKeys.trips.all,
      queryKeys.dashboard.all,
      queryKeys.drivers.all,
      queryKeys.changeLog.all,
    ],
    errorTitle: 'Could not update the trip',
    onSuccess: () => setQuickAction(null),
  })

  const columns = useMemo<ColumnDef<AdminTrip, unknown>[]>(
    () => [
      {
        id: 'bookingReference',
        header: 'Reference',
        meta: { sortKey: 'bookingReference', mobileLabel: 'Reference' },
        cell: ({ row }) => (
          <StackedCell
            primary={
              <span className={styles.reference}>{row.original.bookingReference ?? `#${row.original.id}`}</span>
            }
            secondary={
              row.original.packageName ||
              row.original.addOns.any ||
              hasAreaChips(row.original.areaSurcharge) ? (
                <>
                  {row.original.packageName}
                  {row.original.addOns.any ? (
                    <span style={{ display: 'block', marginTop: 2 }}>
                      <AddOnBadges addOns={row.original.addOns} />
                    </span>
                  ) : null}
                  {hasAreaChips(row.original.areaSurcharge) ? (
                    <span style={{ display: 'block', marginTop: 2 }}>
                      <AreaSurchargeBadge area={row.original.areaSurcharge} />{' '}
                      <LateChargeBadge area={row.original.areaSurcharge} />
                    </span>
                  ) : null}
                </>
              ) : undefined
            }
          />
        ),
      },
      {
        id: 'userName',
        header: 'Passenger',
        meta: { sortKey: 'userName', mobileLabel: 'Passenger' },
        cell: ({ row }) => (
          <StackedCell primary={row.original.userName ?? '—'} secondary={row.original.userPhone ?? undefined} />
        ),
      },
      {
        id: 'driverName',
        header: 'Driver',
        meta: { sortKey: 'driverName', mobileLabel: 'Driver' },
        cell: ({ row }) =>
          row.original.driverName ? (
            <StackedCell primary={row.original.driverName} secondary={row.original.driverPhone ?? undefined} />
          ) : (
            <span className={styles.muted}>Unassigned</span>
          ),
      },
      {
        id: 'route',
        header: 'Route',
        meta: { mobileLabel: 'Route', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={truncate(row.original.pickupAddress, 34)}
            secondary={`→ ${truncate(row.original.dropoffAddress, 34, 'No drop-off')}`}
          />
        ),
      },
      {
        id: 'startTime',
        header: 'Scheduled',
        meta: { sortKey: 'startTime', mobileLabel: 'Scheduled' },
        cell: ({ row }) => (
          <StackedCell
            primary={formatDateTimeCompact(row.original.startTime)}
            secondary={
              row.original.endTime ? `ends ${formatDateTimeCompact(row.original.endTime, '—')}` : 'no end time'
            }
          />
        ),
      },
      {
        id: 'actual',
        header: 'Actual',
        meta: { sortKey: 'startedAt', mobileLabel: 'Actual', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={
              row.original.startedAt ? (
                formatDateTimeCompact(row.original.startedAt)
              ) : (
                <span className={styles.muted}>Not started</span>
              )
            }
            secondary={row.original.endedAt ? `ended ${formatDateTimeCompact(row.original.endedAt, '—')}` : undefined}
          />
        ),
      },
      {
        // The booking's charge, not the trip's total — those differ once extra
        // hours are added, and the payment record is about what was collected
        // up front.
        id: 'payment',
        header: 'Amount',
        meta: { mobileLabel: 'Amount', align: 'right', hideOnMobile: true },
        cell: ({ row }) => <PaymentAmountCell payment={row.original.payment} />,
      },
      {
        id: 'paymentMethod',
        header: 'Payment',
        meta: { mobileLabel: 'Payment' },
        cell: ({ row }) => <PaymentMethodCell payment={row.original.payment} />,
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sortKey: 'status', mobileLabel: 'Status' },
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '400px' },
        cell: ({ row }) => (
          <RowActions>
            {/*
              Offered on every row, whatever the status: where a journey ran is
              as much a question about a completed trip as a scheduled one. The
              dialog itself handles a booking that carries no coordinates.
            */}
            <Button
              size="sm"
              variant="subtle"
              iconLeft={<Map aria-hidden="true" />}
              onClick={() => setViewingRoute(toRouteTarget(row.original))}
            >
              Route
            </Button>
            {row.original.canStart ? (
              <Button
                size="sm"
                variant="subtle"
                iconLeft={<CirclePlay aria-hidden="true" />}
                onClick={() => setQuickAction({ trip: row.original, action: 'start' })}
              >
                Start
              </Button>
            ) : null}
            {row.original.canEnd ? (
              <Button
                size="sm"
                variant="danger"
                iconLeft={<CircleCheck aria-hidden="true" />}
                onClick={() => setQuickAction({ trip: row.original, action: 'end' })}
              >
                End
              </Button>
            ) : null}
            {row.original.canModifyTimes ? (
              <Button
                size="sm"
                variant="subtle"
                iconLeft={<Clock aria-hidden="true" />}
                onClick={() =>
                  setRescheduling({
                    kind: 'trip',
                    id: row.original.id,
                    reference: row.original.bookingReference,
                    customerName: row.original.userName,
                    startTime: row.original.startTime,
                    endTime: row.original.endTime,
                  })
                }
              >
                Times
              </Button>
            ) : null}
            {/*
              Cancelling acts on the booking behind the trip, so it needs the
              request id. This row action is the one a confirmed, driver-assigned
              trip never had: the approval queue only offered Reject while a
              request was still pending, so once it was approved the office had
              no way to call it off at all.
            */}
            {row.original.canCancel && row.original.requestId ? (
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<CircleSlash aria-hidden="true" />}
                onClick={() => setCancelling(row.original.requestId)}
              >
                Cancel
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              iconRight={<ArrowRight aria-hidden="true" />}
              onClick={() => setSelected(row.original)}
            >
              Details
            </Button>
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminTrip>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Reference', value: (row) => row.bookingReference ?? '' },
      { header: 'Passenger', value: (row) => row.userName ?? '' },
      { header: 'Passenger phone', value: (row) => row.userPhone ?? '' },
      { header: 'Driver', value: (row) => row.driverName ?? '' },
      { header: 'Driver phone', value: (row) => row.driverPhone ?? '' },
      { header: 'Category', value: (row) => row.categoryName ?? '' },
      { header: 'Package', value: (row) => row.packageName ?? '' },
      { header: 'Add-ons', value: (row) => describeAddOns(row.addOns) },
      { header: 'Add-ons amount', value: (row) => (row.addOns.any ? row.addOns.totalAmount : '') },
      { header: 'Area surcharge', value: (row) => describeAreaSurcharge(row.areaSurcharge) },
      {
        header: 'Area surcharge amount',
        value: (row) => (row.areaSurcharge.any ? row.areaSurcharge.total : ''),
      },
      { header: 'Late-night charge', value: (row) => lateChargeExportValue(row.areaSurcharge) },
      { header: 'Pickup', value: (row) => row.pickupAddress ?? '' },
      { header: 'Drop-off', value: (row) => row.dropoffAddress ?? '' },
      { header: 'Scheduled start', value: (row) => formatDateTimeCompact(row.startTime, '') },
      { header: 'Actual start', value: (row) => formatDateTimeCompact(row.startedAt, '') },
      { header: 'Scheduled end', value: (row) => formatDateTimeCompact(row.endTime, '') },
      { header: 'Actual end', value: (row) => formatDateTimeCompact(row.endedAt, '') },
      { header: 'Status', value: (row) => row.status ?? '' },
      { header: 'Base fare', value: (row) => row.basePrice ?? '' },
      { header: 'Extension hours', value: (row) => row.extraHoursCount ?? 0 },
      { header: 'Total', value: (row) => row.totalPrice ?? '' },
      { header: 'Booking charge', value: (row) => row.payment.totalAmount },
      { header: 'Paid with points', value: (row) => row.payment.pointsUsed },
      { header: 'Paid externally', value: (row) => row.payment.externalAmount },
      { header: 'Payment method', value: (row) => row.payment.paymentMethodCode ?? row.paymentMethod ?? '' },
      { header: 'Payment status', value: (row) => row.payment.paymentStatus ?? '' },
      { header: 'Verification', value: (row) => row.payment.verificationStatus ?? '' },
      { header: 'Verified by', value: (row) => row.payment.verifiedByAdminName ?? '' },
      { header: 'Verified at', value: (row) => formatDateTimeCompact(row.payment.verifiedAt, '') },
      { header: 'Base payment status', value: (row) => row.basePaymentStatus ?? '' },
    ],
    [],
  )

  const page = tripsQuery.data

  return (
    <>
      <PageHeader
        title="Trips"
        description="Every confirmed journey. Reassign a driver, move the times, cancel a booking a customer has called off, or start and end a trip — the actions offered on each row are the ones its current status allows."
        meta={
          page ? (
            <>
              <span>{page.totalElements} matching trips</span>
              <span className={styles.legend}>
                <span className={styles.legendDot} /> Scheduled vs. actual times shown side by side
              </span>
            </>
          ) : null
        }
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search reference, passenger, driver or address…"
              label="Search trips"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-trips"
              title="Drive For Me — Trips"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchTrips({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Status"
            value={table.filters.status}
            onChange={(value) => table.setFilter('status', value)}
            options={TRIP_STATUSES.map((status) => ({ value: status, label: humanizeEnum(status) }))}
          />
          <FilterSelect
            label="Method"
            value={table.filters.paymentMethod}
            onChange={(value) => table.setFilter('paymentMethod', value)}
            options={PAYMENT_METHODS.map((method) => ({
              value: method,
              label: describePaymentMethod({ paymentMethodCode: method, paymentMethod: method }),
            }))}
          />
          <FilterSelect
            label="Verification"
            value={table.filters.verificationStatus}
            onChange={(value) => table.setFilter('verificationStatus', value)}
            options={PAYMENT_VERIFICATION_STATUSES.map((status) => ({
              value: status,
              label: describeVerificationStatus(status),
            }))}
          />
          <FilterDate
            label="Start from"
            value={table.filters.from}
            onChange={(value) => table.setFilter('from', value)}
            max={table.filters.to || undefined}
          />
          <FilterDate
            label="Start to"
            value={table.filters.to}
            onChange={(value) => table.setFilter('to', value)}
            min={table.filters.from || undefined}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me trips"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={tripsQuery.isLoading}
          isFetching={tripsQuery.isFetching && !tripsQuery.isLoading}
          error={tripsQuery.isError ? tripsQuery.error : undefined}
          onRetry={() => void tripsQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No trips match these filters' : 'No trips yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try a different status or widen the date range.'
              : 'Trips are created when a trip request is approved and a driver is assigned.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="trip"
            disabled={tripsQuery.isFetching}
          />
        ) : null}
      </Card>

      <TripDetailsDrawer trip={selected} onClose={() => setSelected(null)} />

      <RouteMapDialog target={viewingRoute} onClose={() => setViewingRoute(null)} />

      <EditScheduleDialog target={rescheduling} onClose={() => setRescheduling(null)} />

      <CancelBookingDialog tripRequestId={cancelling} onClose={() => setCancelling(null)} />

      <ConfirmDialog
        open={quickAction !== null}
        title={quickAction?.action === 'start' ? 'Start this trip?' : 'End this trip?'}
        message={
          quickAction ? (
            <>
              Trip <strong>{quickAction.trip.bookingReference ?? `#${quickAction.trip.id}`}</strong> for{' '}
              <strong>{quickAction.trip.userName ?? 'this passenger'}</strong>
              {quickAction.action === 'start'
                ? ' will be marked as started, with the actual start time recorded as now.'
                : ' will be marked as completed, with the actual end time recorded as now. This cannot be undone.'}
            </>
          ) : (
            ''
          )
        }
        confirmLabel={quickAction?.action === 'start' ? 'Start trip' : 'End trip'}
        tone={quickAction?.action === 'end' ? 'danger' : 'primary'}
        loading={lifecycleMutation.isPending}
        onConfirm={() => quickAction && lifecycleMutation.mutate(quickAction)}
        onCancel={() => setQuickAction(null)}
      />
    </>
  )
}
